import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { createQuote, updateQuote } from '../services/api';
import { useQuoteStore, type Quote } from '../stores/quoteStore';
import { useCustomerStore } from '../stores/customerStore';
import { Input, CustomSelect, Button, FormError, LoadingButton, dialogButtonClass, DialogShell, DialogHeader, DialogFooter, FormFieldWithError } from '@redpanda/ui';
import { toast } from 'react-hot-toast';
import { notifyDataChanged } from '../utils/dataEvents';
import { useTranslation } from 'react-i18next';
import { canEditArchivedCustomerData } from '../utils/customerUtils';


interface QuoteItem {
  serviceName: string;
  quantity: number;
  unitPrice: number;
}

interface QuoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  quote?: Quote; // 型別與 quoteStore 同一份定義
  preselectedCustomerId?: string;
}

interface FormData {
  customerId: string;
  title: string;
  status: string;
  items: QuoteItem[];
}

const QuoteDialog: React.FC<QuoteDialogProps> = ({
  isOpen,
  onClose,
  quote,
  preselectedCustomerId,
}) => {
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors }, setValue, reset, watch } = useForm<FormData>();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [items, setItems] = useState<QuoteItem[]>([{ serviceName: '', quantity: 1, unitPrice: '' as any }]);
  const [itemErrors, setItemErrors] = useState<any>({});
  
  const { updateQuote: updateQuoteInStore, addQuote: addQuoteToStore, replaceQuote, removeQuote } = useQuoteStore();
  const { allCustomers, isLoadingAllCustomers, fetchAllCustomers } = useCustomerStore();


  const isEditMode = !!quote;
  
  // 檢查是否可以編輯（封存客戶的關聯資料不能編輯）
  const canEdit = !isEditMode || canEditArchivedCustomerData(quote?.customer);
  
  // 監聽表單值變化
  const watchedCustomerId = watch('customerId', '');
  const watchedStatus = watch('status', 'DRAFT');

  // 載入客戶資料
  useEffect(() => {
    if (isOpen) {
      fetchAllCustomers();
    }
  }, [isOpen, fetchAllCustomers]);

  // 設置表單初始值（useLayoutEffect：繪製前帶入，編輯時不會先閃一格空白欄位）
  useLayoutEffect(() => {
    if (isOpen) {
      if (quote) {
        // 編輯模式
        setValue('customerId', quote.customerId);
        setValue('title', quote.title);
        setValue('status', quote.status);
        setItems(quote.items || [{ serviceName: '', quantity: 1, unitPrice: '' as any }]);
      } else {
        // 新增模式
        reset();
        if (preselectedCustomerId) {
          setValue('customerId', preselectedCustomerId);
        }
        setValue('status', 'DRAFT');
        setItems([{ serviceName: '', quantity: 1, unitPrice: '' as any }]);
      }
    }
  }, [isOpen, quote, preselectedCustomerId, setValue, reset]);


  const handleClose = () => {
    setSubmitError(null);
    setItemErrors({});
    reset();
    setItems([{ serviceName: '', quantity: 1, unitPrice: '' as any }]);
    onClose();
  };

  const handleItemChange = (index: number, field: keyof QuoteItem, value: string | number) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { serviceName: '', quantity: 1, unitPrice: '' as any }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
    }
  };

  const onSubmit = async (data: FormData) => {
    // 檢查是否可以編輯封存客戶的資料
    if (isEditMode && !canEdit) {
      toast.error(t('errors.business.cannotEditArchivedCustomerData', '無法編輯封存客戶的關聯資料'));
      return;
    }
    
    // 驗證項目，使用與原版相同的邏輯
    let validationErrors: any = {};
    
    // 檢查項目是否為空或無效
    if (items.length === 0) {
      validationErrors.items = '報價項目為必填';
    } else {
      items.forEach((item, index) => {
        if (!item.serviceName.trim()) {
          if (!validationErrors.items) validationErrors.items = {};
          if (!validationErrors.items[index]) validationErrors.items[index] = {};
          validationErrors.items[index].serviceName = '服務名稱為必填';
        }
        if (item.quantity <= 0) {
          if (!validationErrors.items) validationErrors.items = {};
          if (!validationErrors.items[index]) validationErrors.items[index] = {};
          validationErrors.items[index].quantity = '數量必須大於0';
        }
        if (item.unitPrice < 0) {
          if (!validationErrors.items) validationErrors.items = {};
          if (!validationErrors.items[index]) validationErrors.items[index] = {};
          validationErrors.items[index].unitPrice = '單價不能為負數';
        }
      });
    }

    // 如果有驗證錯誤，設置項目錯誤狀態並返回
    if (Object.keys(validationErrors).length > 0) {
      setItemErrors(validationErrors.items || {});
      if (validationErrors.items && typeof validationErrors.items === 'string') {
        setSubmitError(validationErrors.items);
      } else {
        setSubmitError(null); // 不顯示統一錯誤訊息，讓各欄位顯示具體錯誤
      }
      return;
    }

    // 清除之前的錯誤
    setItemErrors({});

    setSubmitError(null);

    const submitData = {
      customerId: data.customerId,
      title: data.title.trim(),
      status: data.status,
      items: items.map(item => ({
        serviceName: item.serviceName.trim(),
        quantity: Number(item.quantity),
        unitPrice: Number(item.unitPrice),
      })),
    };

    if (isEditMode) {
      // UPDATE：保存原始值以供回滾
      const originalQuote = { ...quote };

      // 樂觀更新 + 立即關閉
      updateQuoteInStore(quote.id, {
        title: data.title.trim(),
        status: data.status,
        totalAmount: items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0),
        items: items.map(item => ({
          serviceName: item.serviceName.trim(),
          quantity: Number(item.quantity),
          unitPrice: Number(item.unitPrice),
        }))
      });
      handleClose();

      // 背景執行 API 更新
      updateQuote(quote.id, submitData).then(() => {
        toast.success(t('common.messages.updateSuccess'));
        notifyDataChanged('quotes');
      }).catch((error: any) => {
        console.error('報價更新失敗:', error);
        // 回滾到原始值
        updateQuoteInStore(quote.id, originalQuote);
        toast.error(error.response?.data?.error || t('common.messages.updateFailed'));
      });
    } else {
      // CREATE：臨時 id 樂觀新增
      const tempId = `temp-${Date.now()}`;
      const selectedCustomer = allCustomers.find(c => c.id === data.customerId);
      const optimisticQuote = {
        id: tempId,
        title: data.title.trim(),
        customerId: data.customerId,
        status: data.status,
        totalAmount: items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0),
        items: submitData.items,
        customer: selectedCustomer ? {
          id: selectedCustomer.id,
          name: selectedCustomer.name,
          userId: (selectedCustomer as any).userId || '',
          archived: selectedCustomer.archived,
        } : { id: data.customerId, name: '', userId: '' },
        createdAt: new Date().toISOString(),
      } as any;

      addQuoteToStore(optimisticQuote);
      handleClose();

      createQuote(submitData).then((response) => {
        if (response?.data) {
          replaceQuote(tempId, response.data);
        }
        toast.success(t('common.messages.createSuccess'));
        notifyDataChanged('quotes');
      }).catch((error: any) => {
        console.error('報價建立失敗:', error);
        removeQuote(tempId);
        toast.error(error.response?.data?.error || t('common.messages.createFailed'));
      });
    }
  };

  if (!isOpen) return null;

  return (
    <DialogShell onClose={handleClose} className="max-w-4xl">
      <DialogHeader title={isEditMode ? t('quotes.edit.title', { ns: 'pages' }) : t('quotes.add.title', { ns: 'pages' })} onClose={handleClose} closeLabel={t('common.actions.close')} />

      {/* 對話框內容 */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-6">
          {/* 封存客戶警告提示 */}
          {isEditMode && !canEdit && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    {t('common.warnings.archivedCustomerData')}
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                    <p>{t('common.warnings.archivedCustomerDataDetail')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <FormFieldWithError
            label={t('common.forms.labels.title')}
            required={true}
            error={errors.title?.message}
            htmlFor="title"
          >
            <Input
              type="text"
              id="title"
              {...register('title', { 
                required: t('common.forms.validation.required'),
                maxLength: { value: 200, message: t('common.forms.validation.maxLength', { length: 200 }) }
              })}
              variant={errors.title ? "error" : "default"}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('common.forms.labels.customer')}
            required={true}
            error={errors.customerId?.message}
            htmlFor="customerId"
          >
            <CustomSelect
              id="customerId"
              {...register('customerId', { required: t('common.forms.validation.required') })}
              value={watchedCustomerId}
              onChange={(value) => setValue('customerId', value)}
              variant={errors.customerId ? "error" : "default"}
              disabled={isLoadingAllCustomers}
              placeholder={t('common.forms.placeholders.selectCustomer')}
              options={[
                ...(isLoadingAllCustomers ? [{ value: 'loading', label: t('common.status.loading'), disabled: true }] : []),
                ...allCustomers
                  .filter(customer => customer.archived !== true)
                  .sort((a, b) => new Date(b.createdAt || b.updatedAt || '').getTime() - new Date(a.createdAt || a.updatedAt || '').getTime())
                  .map(customer => ({ 
                    value: customer.id, 
                    label: `${customer.name}${customer.company ? ` (${customer.company})` : ''}` 
                  }))
              ]}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('common.forms.labels.status')}
            error={errors.status?.message}
            htmlFor="status"
          >
            <CustomSelect
              id="status"
              {...register('status', { required: t('common.forms.validation.required') })}
              value={watchedStatus}
              onChange={(value) => setValue('status', value)}
              variant={errors.status ? "error" : "default"}
              placeholder={t('common.forms.placeholders.selectStatus')}
              options={[
                { value: 'DRAFT', label: t('quotes.status.draft', { ns: 'pages' }) },
                { value: 'SENT', label: t('quotes.status.sent', { ns: 'pages' }) },
                { value: 'ACCEPTED', label: t('quotes.status.accepted', { ns: 'pages' }) },
                { value: 'REJECTED', label: t('quotes.status.rejected', { ns: 'pages' }) },
                { value: 'COMPLETED', label: t('quotes.status.completed', { ns: 'pages' }) }
              ]}
            />
          </FormFieldWithError>

          {/* 報價項目 */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-fg">{t('quotes.form.items', { ns: 'pages' })} <span className="text-fg ml-1">*</span></h3>
              {/* 對話框只留一顆主要按鈕（送出），新增項目用次要（§3-5） */}
              <Button type="button" onClick={addItem} size="sm" variant="secondary" className="inline-flex items-center">
                <PlusIcon className="h-4 w-4 mr-1" />
                {t('quotes.form.addItem', { ns: 'pages' })}
              </Button>
            </div>
          
            {/* 標題行 */}
            <div className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-5">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('quotes.form.item.serviceName', { ns: 'pages' })}
                </span>
              </div>
              <div className="col-span-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('quotes.form.item.quantity', { ns: 'pages' })}
                </span>
              </div>
              <div className="col-span-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('quotes.form.item.unitPrice', { ns: 'pages' })}
                </span>
              </div>
              <div className="col-span-2">
                {/* 操作列 */}
              </div>
            </div>
          
            {items.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-4 items-start">
                <div className="col-span-5">
                  <div className="space-y-1">
                    <Input 
                      type="text" 
                      value={item.serviceName} 
                      onChange={(e) => handleItemChange(index, 'serviceName', e.target.value)}
                      variant={itemErrors[index]?.serviceName ? "error" : "default"}
                    />
                    {itemErrors[index]?.serviceName && (
                      <FormError>{itemErrors[index].serviceName}</FormError>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="space-y-1">
                    <Input 
                      type="text" 
                      value={item.quantity} 
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*$/.test(value)) {
                          handleItemChange(index, 'quantity', Number(value) || 1);
                        }
                      }}
                      variant={itemErrors[index]?.quantity ? "error" : "default"}
                      placeholder="1"
                    />
                    {itemErrors[index]?.quantity && (
                      <FormError>{itemErrors[index].quantity}</FormError>
                    )}
                  </div>
                </div>
                <div className="col-span-3">
                  <div className="space-y-1">
                    <Input 
                      type="text" 
                      value={item.unitPrice} 
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d{0,2}$/.test(value)) {
                          handleItemChange(index, 'unitPrice', Number(value) || 0);
                        }
                      }}
                      variant={itemErrors[index]?.unitPrice ? "error" : "default"}
                      placeholder="0"
                    />
                    {itemErrors[index]?.unitPrice && (
                      <FormError>{itemErrors[index].unitPrice}</FormError>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  <Button
                    type="button"
                    onClick={() => removeItem(index)}
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    disabled={items.length === 1}
                    aria-label={t('common.actions.delete')}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

          </div>

          {submitError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
              {submitError}
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={handleClose}
            className={dialogButtonClass.cancel}
          >
            {t('common.actions.cancel')}
          </button>
          <LoadingButton
            type="submit"
            isEditMode={isEditMode}
            disabled={isLoadingAllCustomers || !canEdit}
            t={t}
          />
        </DialogFooter>
      </form>
    </DialogShell>
  );
};

export default QuoteDialog;